import { useState } from 'react';

/**
 * 메뉴·탭 순서 바꾸기 창(게시판 #160). 끌어다 놓거나 ▲▼ 로 옮기고 「저장」.
 * 저장은 계정에 남는다(`lib/menuOrder.ts`) — 다른 PC 에서 로그인해도 같은 순서다.
 * 「기본 순서로」는 저장값을 지운다(그 뒤 새로 생기는 메뉴도 기본 자리에 들어간다).
 */
export default function MenuOrderEditor({ title, items, onSave, onReset, onClose }: {
  title: string;
  items: { id: string; label: string }[];
  onSave: (ids: string[]) => void;
  onReset: () => void;
  onClose: () => void;
}) {
  const [list, setList] = useState(items);
  const [dragAt, setDragAt] = useState<number | null>(null);

  const move = (from: number, to: number) => {
    if (to < 0 || to >= list.length || from === to) return;
    setList((prev) => {
      const next = [...prev];
      const [it] = next.splice(from, 1);
      next.splice(to, 0, it!);
      return next;
    });
  };

  return (
    <div className="card p-4">
      <div className="mb-1 text-base font-bold">{title}</div>
      <p className="mb-3 text-xs text-ink-soft">줄을 끌어다 놓거나 ▲▼ 로 옮긴 뒤 저장하세요. 내 계정에만 적용됩니다.</p>
      <ol className="max-h-[60vh] space-y-1 overflow-y-auto">
        {list.map((it, i) => (
          <li key={it.id}
              draggable
              onDragStart={() => setDragAt(i)}
              onDragOver={(e) => {
                e.preventDefault();
                if (dragAt !== null && dragAt !== i) { move(dragAt, i); setDragAt(i); }
              }}
              onDragEnd={() => setDragAt(null)}
              className={`flex cursor-grab items-center gap-2 rounded-lg border px-3 py-1.5 text-sm ${
                dragAt === i ? 'border-brand-500 bg-brand-50' : 'border-line bg-surface'}`}>
            <span className="text-ink-faint" aria-hidden>⠿</span>
            <span className="w-6 text-right text-xs text-ink-faint">{i + 1}</span>
            <span className="flex-1 truncate">{it.label}</span>
            <button type="button" className="btn-ghost px-2 py-0.5" disabled={i === 0}
                    aria-label={`${it.label} 위로`} onClick={() => move(i, i - 1)}>▲</button>
            <button type="button" className="btn-ghost px-2 py-0.5" disabled={i === list.length - 1}
                    aria-label={`${it.label} 아래로`} onClick={() => move(i, i + 1)}>▼</button>
          </li>
        ))}
      </ol>
      <div className="mt-4 flex items-center gap-2">
        <button type="button" className="btn-ghost" onClick={() => { onReset(); onClose(); }}>기본 순서로</button>
        <div className="ml-auto flex gap-2">
          <button type="button" className="btn-ghost" onClick={onClose}>닫기</button>
          <button type="button" className="btn-primary" onClick={() => { onSave(list.map((x) => x.id)); onClose(); }}>저장</button>
        </div>
      </div>
    </div>
  );
}
